import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from './client';
import { useScopeFilter } from '@/contexts/ClientScopeContext';

/**
 * Network module — Sophos and UniFi are SEPARATE views with their own data (server/network/routes.ts
 * `/sophos`, `/unifi`). Only the company mapping is shared.
 */

interface Company { companyId: number | null; companyName: string | null; mappingIgnored: boolean }
export interface Bucket { id: string; label: string; value: number }

// =================== Sophos ===================

export interface SophosLicence { name: string; code: string | null; kind: 'subscription' | 'trial' | 'perpetual'; start: string | null; end: string | null; daysLeft: number | null }

export interface SophosFirewall extends Company {
  id: string; tenantId: string; tenantName: string | null; serial: string; hostname: string; model: string | null;
  firmware: string | null; firmwareMajor: string | null; connected: boolean; suspended: boolean; managing: string | null; reporting: string | null;
  externalIps: string[]; stateChangedAt: string | null; lastSeen: string | null; ha: boolean;
  subscriptionEnd: string | null; subscriptionDaysLeft: number | null; subscriptionState: 'ok' | 'expiring' | 'expired' | 'none';
  licences: SophosLicence[]; openAlerts: number;
}

export interface SophosAlert extends Company { id: string; serial: string | null; hostname: string | null; tenantId: string; tenantName: string | null; description: string; severity: string; category: string | null; type: string | null; raisedAt: string | null }

export interface SophosTenant extends Company {
  id: string; name: string; billingType: string | null; status: string | null; contactDomain: string | null; firewalls: number;
  backup: { score: number | null; total: number | null; notOnRecommended: number | null; frequency: string | null } | null;
  loaded: boolean; fetchedAt: string | null; error: string | null;
}

export interface SophosRenewal extends SophosLicence { serial: string; hostname: string; model: string | null; companyId: number | null; companyName: string | null; tenantName: string | null }

export interface SophosView {
  tenants: SophosTenant[];
  firewalls: SophosFirewall[];
  alerts: SophosAlert[];
  renewals: SophosRenewal[];
  unlisted: { serial: string; model: string | null; lastSeen: string | null; licences: SophosLicence[] }[];
  progress: { running: boolean; done: number; total: number; finishedAt: string | null };
  warnDays: number;
  latestMajor: string | null;
  kpis: { firewalls: number; connected: number; disconnected: number; subscriptionsExpired: number; subscriptionsExpiring: number; noSubscription: number; alerts: number; behindFirmware: number; backupNotCompliant: number; tenants: number; unmapped: number };
  charts: { firmware: Bucket[]; models: Bucket[]; alertTypes: Bucket[] };
}

/** `scoped`: the server narrows to the picked client so KPIs and charts match (they're computed there). */
export function useSophos(opts: { scoped?: boolean } = {}) {
  const { companyId } = useScopeFilter();
  const cid = opts.scoped ? companyId : null;
  return useQuery({
    queryKey: ['network', 'sophos', cid],
    queryFn: () => api.get<{ configured: boolean; data: SophosView | null }>(`/api/network/sophos${cid != null ? `?companyId=${cid}` : ''}`),
    placeholderData: (prev) => prev,
    staleTime: 2 * 60_000,
    // Poll while the first hourly sweep is still running after a restart.
    refetchInterval: (q) => (q.state.data?.configured && !q.state.data.data?.progress.finishedAt ? 15_000 : false),
  });
}

// =================== UniFi ===================

export interface UnifiConsole extends Company {
  id: string; name: string; kind: 'console' | 'network-server'; model: string | null; hardwareName: string | null; connected: boolean;
  unifiOs: string | null; networkApp: string | null; firmwareUpdate: string | null; autoUpdate: boolean; releaseChannel: string | null;
  lastBackup: string | null; publicIp: string | null; stateChangedAt: string | null; owner: boolean; sites: number;
}

export interface UnifiSite extends Company {
  id: string; hostId: string; name: string; label: string; hostName: string | null; hostModel: string | null; hostConnected: boolean;
  devices: number; offline: number; offlineGateways: number; pendingUpdates: number; wifiClients: number; wiredClients: number; guestClients: number;
  wanIp: string | null; isp: string | null; wanUptimePct: number | null; txRetryPct: number | null; internetIssues: number; gateway: string | null; ips: string | null;
}

export interface UnifiDevice extends Company {
  id: string; hostId: string; hostName: string; hostConnected: boolean; name: string; model: string | null; productLine: string | null;
  ip: string | null; mac: string | null; online: boolean; version: string | null; updateAvailable: boolean; isConsole: boolean; startupTime: string | null;
}

export interface UnifiWan {
  siteId: string; hostId: string; label: string; companyId: number | null; companyName: string | null; isp: string | null; hours: number; noData: boolean;
  avgLatencyMs: number | null; maxLatencyMs: number | null; packetLossPct: number | null; uptimePct: number | null; downtimeMin: number;
  downloadMbps: number | null; uploadMbps: number | null; series: { t: string; latency: number | null; loss: number | null; uptime: number | null }[];
}

export interface UnifiView {
  consoles: UnifiConsole[];
  sites: UnifiSite[];
  devices: UnifiDevice[];
  wan: UnifiWan[];
  fetchedAt: string;
  kpis: {
    consoles: number; consolesDisconnected: number; consoleUpdates: number; noAutoUpdate: number; staleBackups: number; sites: number;
    devices: number; devicesOnline: number; devicesOffline: number; devicesUnknown: number; deviceUpdates: number; clients: number;
    sitesWithWanIssues: number; wanSites: number; wanDegraded: number; wanNoData: number; unmapped: number;
  };
  charts: { productLines: Bucket[]; consoleModels: Bucket[]; unifiOs: Bucket[]; isps: Bucket[] };
}

/** `scoped`: the server narrows to the picked client so KPIs and charts match. */
export function useUnifi(opts: { scoped?: boolean } = {}) {
  const { companyId } = useScopeFilter();
  const cid = opts.scoped ? companyId : null;
  return useQuery({
    queryKey: ['network', 'unifi', cid],
    queryFn: () => api.get<{ configured: boolean; data: UnifiView | null }>(`/api/network/unifi${cid != null ? `?companyId=${cid}` : ''}`),
    staleTime: 2 * 60_000,
    placeholderData: (prev) => prev,
  });
}

// =================== Mapping (shared, admin) ===================

export interface NetMapRow {
  source: 'sophos' | 'unifi';
  id: string;
  label: string;
  detail: string | null;
  mapping: { companyId: number | null; companyName: string | null; ignored: boolean; mappedBy: string; mappedAt: string } | null;
  suggestion: { companyId: number; companyName: string; strength: 'strong' | 'possible'; reasons: string[] } | null;
}

export function useNetworkMap() {
  return useQuery({ queryKey: ['network', 'map'], queryFn: () => api.get<{ items: NetMapRow[] }>('/api/network/map').then((r) => r.items), staleTime: 60_000 });
}

export function useSetNetworkMapping() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ source, id, value }: { source: 'sophos' | 'unifi'; id: string; value: { companyId: number; companyName: string } | { ignored: true } | null }) =>
      value ? api.put(`/api/network/map/${source}/${encodeURIComponent(id)}`, value) : api.del(`/api/network/map/${source}/${encodeURIComponent(id)}`),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ['network'] }); qc.invalidateQueries({ queryKey: ['insights'] }); },
  });
}

export function useAcceptNetworkMatches() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (items: { source: 'sophos' | 'unifi'; id: string; companyId: number; companyName: string }[]) => api.post<{ saved: number }>('/api/network/map/accept', { items }),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ['network'] }); qc.invalidateQueries({ queryKey: ['insights'] }); },
  });
}
